import { showToast, formatError } from "../lib/dom";
import { trackEvent } from "../lib/analytics";
import * as projectImageCache from "../lib/projectImageCache";
import { humanizeProjectType } from "./closet";

interface PreviewArgs {
  requestId: string;
  type: string;
  outputUrl: string;
  fallbackUrls?: string[];
}

interface PreviewState {
  overlay: HTMLElement;
  dialog: HTMLElement;
  imageEl: HTMLImageElement;
  shareBtn: HTMLButtonElement;
  saveBtn: HTMLButtonElement;
  cleanup: Array<() => void>;
  closed: boolean;
  args: PreviewArgs;
  imageBlobUrl: string | null;
  previousFocus: HTMLElement | null;
}

let active: PreviewState | null = null;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function lockBodyScroll(): () => void {
  const previous = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = previous;
  };
}

function trapFocus(dialog: HTMLElement): () => void {
  function handle(event: KeyboardEvent): void {
    if (event.key !== "Tab") return;
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const activeElement = document.activeElement as HTMLElement | null;
    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  dialog.addEventListener("keydown", handle);
  return () => dialog.removeEventListener("keydown", handle);
}

function buildModal(caption: string): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "preview-overlay";
  overlay.innerHTML = `
    <div
      class="preview-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
    >
      <header class="preview-header">
        <h2 id="preview-title" class="preview-title">${escapeHtml(caption)}</h2>
        <button class="preview-close" type="button" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </header>
      <div class="preview-body">
        <div class="preview-image-wrap">
          <div class="preview-skeleton" aria-hidden="true"></div>
          <img class="preview-image" alt="Generated result" />
        </div>
      </div>
      <footer class="preview-actions">
        <button class="button button-secondary preview-share" type="button">Share</button>
        <button class="button button-secondary preview-save" type="button">Save</button>
      </footer>
    </div>
  `;
  return overlay;
}

async function resolveImageSource(
  args: PreviewArgs,
): Promise<{ src: string; revoke: () => void }> {
  try {
    const { blobUrl, revoke } = await projectImageCache.getOrFetch(
      args.requestId,
      args.outputUrl,
      args.fallbackUrls,
    );
    return { src: blobUrl, revoke };
  } catch (error) {
    console.warn("Preview cache lookup failed, using network URL:", error);
    return {
      src: args.outputUrl,
      revoke: () => {
        /* network URL — nothing to revoke */
      },
    };
  }
}

function filenameFor(requestId: string): string {
  return `aiclotheschanger-${requestId}.png`;
}

async function handleShare(): Promise<void> {
  if (!active) return;
  const { outputUrl, requestId, type } = active.args;
  const shareData: ShareData = {
    url: outputUrl,
    title: "AI Clothes Changer",
    text: `${humanizeProjectType(type)} from AI Clothes Changer`,
  };

  const navigatorWithShare = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>;
  };
  if (typeof navigatorWithShare.share === "function") {
    try {
      await navigatorWithShare.share(shareData);
      trackEvent("closet_project_shared", {
        project_id: requestId,
        project_type: type,
        method: "web_share",
      });
      return;
    } catch (error: unknown) {
      // User cancellation throws AbortError; treat as silent.
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.warn("Web Share failed, falling back to clipboard:", error);
    }
  }

  try {
    await navigator.clipboard.writeText(outputUrl);
    trackEvent("closet_project_shared", {
      project_id: requestId,
      project_type: type,
      method: "clipboard",
    });
    showToast("Link copied");
  } catch (error: unknown) {
    showToast(formatError(error), true);
  }
}

async function handleSave(): Promise<void> {
  if (!active) return;
  const { outputUrl, requestId, type } = active.args;
  const filename = filenameFor(requestId);

  try {
    const response = await fetch(outputUrl, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

    trackEvent("closet_project_saved", {
      project_id: requestId,
      project_type: type,
      method: "blob_download",
    });
  } catch (error: unknown) {
    console.warn("Direct save failed, opening in new tab:", error);
    window.open(outputUrl, "_blank", "noopener");
    showToast("Long-press to save on mobile, right-click on desktop.");
    trackEvent("closet_project_saved", {
      project_id: requestId,
      project_type: type,
      method: "new_tab_fallback",
    });
  }
}

export function openProjectPreview(args: PreviewArgs): void {
  if (active) return;

  const caption = humanizeProjectType(args.type);

  trackEvent("closet_project_preview_opened", {
    project_id: args.requestId,
    project_type: args.type,
  });

  const overlay = buildModal(caption);
  document.body.appendChild(overlay);

  const dialog = overlay.querySelector<HTMLElement>(".preview-dialog");
  const imageEl = overlay.querySelector<HTMLImageElement>(".preview-image");
  const shareBtn = overlay.querySelector<HTMLButtonElement>(".preview-share");
  const saveBtn = overlay.querySelector<HTMLButtonElement>(".preview-save");
  const closeBtn = overlay.querySelector<HTMLButtonElement>(".preview-close");

  if (!dialog || !imageEl || !shareBtn || !saveBtn || !closeBtn) {
    overlay.remove();
    return;
  }

  active = {
    overlay,
    dialog,
    imageEl,
    shareBtn,
    saveBtn,
    cleanup: [],
    closed: false,
    args,
    imageBlobUrl: null,
    previousFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null,
  };

  active.cleanup.push(lockBodyScroll());
  active.cleanup.push(trapFocus(dialog));

  function handleEscape(event: KeyboardEvent): void {
    if (event.key === "Escape") closeProjectPreview();
  }
  document.addEventListener("keydown", handleEscape);
  active.cleanup.push(() => document.removeEventListener("keydown", handleEscape));

  overlay.addEventListener("click", event => {
    if (event.target === overlay) closeProjectPreview();
  });

  closeBtn.addEventListener("click", () => closeProjectPreview());
  shareBtn.addEventListener("click", () => {
    void handleShare();
  });
  saveBtn.addEventListener("click", () => {
    void handleSave();
  });

  imageEl.addEventListener("load", () => {
    if (!active) return;
    overlay.classList.add("preview-image-ready");
  });
  imageEl.addEventListener("error", () => {
    if (!active) return;
    overlay.classList.add("preview-image-ready");
    showToast("Couldn't load the image.", true);
  });

  void resolveImageSource(args).then(({ src, revoke }) => {
    if (!active || active.closed) {
      revoke();
      return;
    }
    active.imageBlobUrl = src.startsWith("blob:") ? src : null;
    active.cleanup.push(revoke);
    imageEl.src = src;
  });

  closeBtn.focus();
}

export function closeProjectPreview(): void {
  if (!active) return;
  active.closed = true;
  active.cleanup.forEach(fn => {
    try {
      fn();
    } catch (error) {
      console.error("Preview cleanup failed:", error);
    }
  });
  active.overlay.remove();
  const restore = active.previousFocus;
  active = null;
  if (restore && restore.isConnected) {
    try {
      restore.focus({ preventScroll: true });
    } catch {
      // Some elements don't support focus options — best effort.
    }
  }
}
